/** @module csi/table/ChartForm */
define([
	"dojo/_base/declare",
	"dojo/store/Memory",
	"csi/_base/underscore",
	"csi/form/Widget",
	"csi/locale",
	"csi/types",
	"csi/widget/FusionChartStore"
], function(declare, Memory, _, FormWidget, locale, types, FusionChartStore) {

	/** General Constants */
	var CHART_SORT_DEFAULT = "default";
	var CHART_SORT_ASC = "valAsc";
	var CHART_SORT_DESC = "valDesc";

	/** Chart Form Fields */
	var CHART_SORT_BY = "ChartSortBy";
	var CHART_GROUP_X = "ChartGroupXHeading";
	var CHART_GROUP_Y = "ChartGroupYHeading";
	var CHART_X_AXIS = "ChartXAxisName";
	var CHART_Y_AXIS = "ChartYAxisName";
	var CHART_VALUE = "ChartValueHeading";
	var CHART_VALUE_2 = "ChartValue2Heading";
	var CHART_NAME = "ChartName";
	var CHART_TEMPLATE = "ChartTemplate";
	var CHART_SHOW_LABELS = "ChartShowLabels";
	var CHART_SHOW_VALUES = "ChartShowValues";
	var CHART_SHOW_PERCENTAGE = "ChartShowPercentageValues";
	const CHART_PERCENTAGE_DECIMAL_PLACES = "ChartPercentageDecimalPlaces";
	const CHART_PERCENTAGE_DECIMAL_PLACES_DEFAULT = 2;

	/**
	 * Static variable to hold "Sort By" Memory Store
	 */
	var _sortByStore = null;

	/**
	 * "Sort By" field values and store
	 * @return {Memory}
	 */
	function getSortByStore() {
		if (!_sortByStore) {
			var sortByValues = [{id: CHART_SORT_DEFAULT, name: locale.resource("ChartSortDefault")},
			{id: CHART_SORT_ASC, name: locale.resource("ChartSortValueAsc")},
			{id: CHART_SORT_DESC, name: locale.resource("ChartSortValueDesc")}];
			_sortByStore = new Memory({data: sortByValues, searchAttr: "label"});
		}
		return _sortByStore;
	}

	function getDecimalsStore() {
		const data = [
			{id: 0, name: "0"},
			{id: 1, name: "1"},
			{id: 2, name: "2"},
			{id: 3, name: "3"},
			{id: 4, name: "4"},
		];
		return new Memory({data});
	}

	/** Charts which might be sorted by value */
	var sortableCharts = {
		"Bar2D": true,
		"Area2D": true,
		"Column2D": true,
		"Doughnut2D": true,
		"Pie2D": true,
		"Line": true
	};

	return declare(FormWidget, {
		"class": "csi-form-view-block",

		// use the value from field even though it's invisible
		bWriteVisible: false,

		/** @property {ToolbarPluginChart} chartPlugin */
		chartPlugin: null,

		/** @property {boolean} bChartValue - indicates if allowed to select chart value */
		bChartValue: false,

		/** @property {boolean} bEditing - indicates if editing the chart */
		bEditing: false,

		/** @property {boolean} bSelectGroupX - indicates if allowed to select attribute for X axis to group by */
		bSelectGroupX: true,

		/** @property {boolean} bSelectGroupY - indicates if allowed to select attribute for Y axis to group by */
		bSelectGroupY: false,

		/** @property {string} typeCharted - plural name of type being charted */
		typeCharted: "",

		/**
		 * Add parameters to field with given name.
		 * @param {string} name
		 * @param {Object} params
		 */
		_addWidgetParams: function(name, params) {
			var field = this.defView.getField(name);
			console.assert(!!field);
			field.widgetParams = params;
		},

		/**
		 * Update axis names based on change to X axis grouping attribute.
		 * @param {View} defView
		 * @param {Field} field
		 * @param {string} x
		 */
		_onChangeGroupX: function(defView, field, x) {
			var item = this.chartPlugin.getGroupStore().get(x);
			var xname = item ? item.name : null;
			var yname = this.getFieldValue(CHART_Y_AXIS);
			var v = this.getFieldValue(CHART_VALUE);

			if (xname && !v) {
				yname = this.typeCharted;
			} else if (!xname && v) {
				xname = this.typeCharted;
			} else if (!x && !v) {
				xname = yname = null;
			}

			this.setFieldValue(CHART_X_AXIS, xname || "");
			this.setFieldValue(CHART_Y_AXIS, yname || "");
		},

		/**
		 * Update axis names based on change to value attribute.
		 * @param {View} defView
		 * @param {Field} field
		 * @param {string} v
		 */
		_onChangeValue: function(defView, field, v) {
			var item = this.chartPlugin.getValueStore().get(v);
			var yname = item ? item.name : null;
			var xname = this.getFieldValue(CHART_X_AXIS);
			var x = this.getFieldValue(CHART_GROUP_X);

			if (yname && !x) {
				xname = this.typeCharted;
			} else if (!yname && x) {
				yname = this.typeCharted;
			} else if (!x && !v) {
				xname = yname = null;
			}

			this.setFieldValue(CHART_X_AXIS, xname || "");
			this.setFieldValue(CHART_Y_AXIS, yname || "");
		},

		/**
		 * Validate chart name is not a duplicate of another chart.
		 * @param {View} defView
		 * @param {Field} field
		 * @param {string} value
		 * @param {Object} params
		 */
		_onValidateName: function(defView, field, value, params) {
			var chartSettings = this.chartPlugin.getView().get("chartSettings") || [];
			var oldName = this.defView.m_formData.getFieldInitialValue(CHART_NAME);

			if (value != oldName) {
				_.some(chartSettings, function(chartData) {
					if (chartData.chartName === value) {
						throw new Error(locale.resource("ChartNameDuplicated"));
					}
				});
			}
		},

		/**
		 * Validate that there is a valid value or group attribute for X axis.
		 * @param {View} defView
		 * @param {Field} field
		 * @param {string} value
		 * @param {Object} params
		 */
		_onValidateValue: function(defView, field, value, params) {
			if (!value && !this.getFieldValue(CHART_GROUP_X)) {
				if (this.bSelectGroupX) {
					throw new Error(locale.resource("ChartGroupAndValueRequired"));
				} else {
					throw new Error(locale.resource("ChartValueRequired"));
				}
			}
		},

		/**
		 * Validate that there is either a value or group attribute for Y axis.
		 * @param {View} defView
		 * @param {Field} field
		 * @param {string} value
		 * @param {Object} params
		 */
		_onValidateValue2: function(defView, field, value, params) {
			if (!value && !this.getFieldValue(CHART_GROUP_Y) && this.bSelectGroupY) {
				throw new Error(locale.resource("ChartStackByRequired"));
			}
		},

		/**
		 * Setup view parameters.
		 */
		_resolveView: function() {
			var plugin = this.chartPlugin;
			var groupStore = plugin.getGroupStore();
			var valueStore = plugin.getValueStore();

			this.addDefaultValue(CHART_SHOW_LABELS, true);

			this.typeCharted = plugin.getTypeCharted();
			this._addWidgetParams(CHART_TEMPLATE, {
				store: FusionChartStore,
				fetchProperties: {
					sort: [{attribute: FusionChartStore.labelProperty}]
				}
			});

			if (groupStore) {
				this._addWidgetParams(CHART_GROUP_X, {store: groupStore});
				this._addWidgetParams(CHART_GROUP_Y, {store: groupStore});
				this.bSelectGroupX = true;
			} else {
				this.bSelectGroupX = false;
			}

			if (valueStore) {
				this._addWidgetParams(CHART_VALUE, {store: valueStore});
				this._addWidgetParams(CHART_VALUE_2, {store: valueStore});
				this.bChartValue = true;
			} else {
				this.bChartValue = false;
			}

			var sortByStore = getSortByStore();
			this._addWidgetParams(CHART_SORT_BY, {store: sortByStore});
			this.addDefaultValue(CHART_SORT_BY, CHART_SORT_DEFAULT);

			// add field events
			this.defView.getField(CHART_NAME).m_onValidate = "_onValidateName";
			this.defView.getField(CHART_VALUE).m_onValidate = "_onValidateValue";
			this.defView.getField(CHART_VALUE_2).m_onValidate = "_onValidateValue2";

			this.defView.getField(CHART_GROUP_X).m_onChange = "_onChangeGroupX";
			this.defView.getField(CHART_VALUE).m_onChange = "_onChangeValue";

			// combine last field with previous one
			this.defView.getField(CHART_SHOW_VALUES).bCombineWithPrevious = true;
			this.defView.getField(CHART_SHOW_PERCENTAGE).bCombineWithPrevious = true;

			this._addWidgetParams(CHART_PERCENTAGE_DECIMAL_PLACES, {store: getDecimalsStore()});
			this.addDefaultValue(CHART_PERCENTAGE_DECIMAL_PLACES, CHART_PERCENTAGE_DECIMAL_PLACES_DEFAULT);

			this.inherited(arguments);
		},

		/**
		 * Update widgets of form as others change.
		 * @param {Field} [field]
		 */
		_updateFieldVisibility: function(field) {
			const chartGroupX = this.getFieldValue(CHART_GROUP_X);
			let chartGroupY = this.getFieldValue(CHART_GROUP_Y);
			const chartTemplate = this.getFieldValue(CHART_TEMPLATE);
			const chartValue2 = this.getFieldValue(CHART_VALUE_2);
			const bMulti = chartTemplate && FusionChartStore.get(chartTemplate).multiseries;
			const bStackedType = chartTemplate && FusionChartStore.get(chartTemplate).stackedType;
			const value2Store = this.getFieldStore(CHART_VALUE_2);
			const bForceStackBy = bStackedType && value2Store && (value2Store.query().length == 1);
			const bSupportPercentage = chartTemplate && FusionChartStore.get(chartTemplate).percentages;
			const bShowValues = this.getFieldValue(CHART_SHOW_VALUES);
			const bShowPercentage = this.getFieldValue(CHART_SHOW_PERCENTAGE);
			const valueStore = this.chartPlugin.getValueStore();
			let bHide = false;

			// in the case of creating the chart(in the wizard), always hide the chart type field so that the user can only be able to change it on the first page
			this.setFieldHidden(CHART_TEMPLATE, !this.bEditing);

			// groupX
			this.setFieldHidden(CHART_GROUP_X, !this.bSelectGroupX);
			this.setFieldRequired(CHART_GROUP_X, bStackedType || !this.bChartValue);

			// groupY
			if (bHide = !bMulti || !chartGroupX || !!chartValue2) {
				this.setFieldValue(CHART_GROUP_Y, "");
				chartGroupY = null;
			}
			this.bSelectGroupY = !bHide;
			this.setFieldHidden(CHART_GROUP_Y, bHide && !bForceStackBy);
			this.setFieldRequired(CHART_GROUP_Y, this.bSelectGroupY);

			// value
			// NOTE: value is not required if CHART_GROUP_Y is required (i.e. multi-series or stacked chart)
			if (bHide = !this.bChartValue) {
				this.setFieldValue(CHART_VALUE, "");
			}

			// if the selected type is stacked type, then value should be optional
			const valueRequired = !bStackedType && !this.bSelectGroupY && (bMulti || !chartGroupX);
			this.setFieldHidden(CHART_VALUE, bHide);
			this.setFieldRequired(CHART_VALUE, valueRequired);

			// value2
			if (bHide = chartGroupX || chartGroupY || !bMulti || !valueStore) {
				this.setFieldValue(CHART_VALUE_2, "");
			}
			this.setFieldHidden(CHART_VALUE_2, bHide || bForceStackBy);
			this.setFieldRequired(CHART_VALUE_2, !bHide);

			// hide show percentage value if percentages are not supported
			if (bHide = !bSupportPercentage) {
				this.setFieldValue(CHART_SHOW_PERCENTAGE, false);
			}
			this.setFieldHidden(CHART_SHOW_PERCENTAGE, bHide);

			var showSortBy = (chartTemplate in sortableCharts);
			this.setFieldHidden(CHART_SORT_BY, !showSortBy);
			if (!showSortBy) {
				this.setFieldValue(CHART_SORT_BY, this.getDefaultValue(CHART_SORT_BY));
			}

			this.setFieldHidden(CHART_PERCENTAGE_DECIMAL_PLACES, !bShowPercentage);
		}
	});
});
